import {useContext, useEffect, useRef, useState} from 'react'

import PropTypes from 'prop-types'

import AtomButton, {atomButtonDesigns, atomButtonShapes, atomButtonSizes} from '@s-ui/react-atom-button'
import {atomIconSizes} from '@s-ui/react-atom-icon'
import Context from '@s-ui/react-context'
import MoleculeCarousel from '@s-ui/react-molecule-carousel'
import IconAngleLeft from '@adv-mt/mt-svg-icons/lib/AngleLeft'
import IconAngleRight from '@adv-mt/mt-svg-icons/lib/AngleRight'
import TitleBasic, {titleBasicColors, titleBasicSizes, titleBasicTags} from '@adv-mt/mt-title-basic'

const NULLISH_COMPONENT = () => null
const NO_OP = () => {}

const DEFAULT_AUTOPLAY_INTERVAL = 5000
const DEFAULT_SLIDE = 0
const DEFAULT_NUM_OF_SLIDES = 1

const BASE_CLASS = 'mt-ListSlides'
const HEADING_CLASS = `${BASE_CLASS}-heading`
const ARROWS_CLASS = `${HEADING_CLASS}Arrows`

export default function ListSlides({
  defaultSlide = DEFAULT_SLIDE,
  hasExternalArrows,
  isFullWidth,
  items,
  itemsToPreload,
  numberOfSlides = DEFAULT_NUM_OF_SLIDES,
  onAfterNextArrowClick = NO_OP,
  onAfterPrevArrowClick = NO_OP,
  title,
  titleNode,
  titleSize = titleBasicSizes.xs,
  autoplay = false,
  autoplayInterval = DEFAULT_AUTOPLAY_INTERVAL,
  isInfinite = false
}) {
  const {i18n} = useContext(Context)
  const [currentSlide, setCurrentSlide] = useState(defaultSlide)
  const [userHasInteracted, setUserHasInteracted] = useState(false)

  const handleUserInteraction = () => {
    if (autoplay && !userHasInteracted) {
      setUserHasInteracted(true)
    }
  }
  const goToPrevSlide = () => {
    const FIRST_ITEM_INDEX = 0
    const hypotheticalSlideIndex = currentSlide - numberOfSlides

    if (isInfinite) {
      const newCurrentSlide =
        hypotheticalSlideIndex < FIRST_ITEM_INDEX ? items.length - numberOfSlides : hypotheticalSlideIndex
      setCurrentSlide(newCurrentSlide)
      return
    }

    const newCurrentSlide = hypotheticalSlideIndex < FIRST_ITEM_INDEX ? FIRST_ITEM_INDEX : hypotheticalSlideIndex

    setCurrentSlide(newCurrentSlide)
  }
  const onPrevClick = () => {
    handleUserInteraction()
    goToPrevSlide()
    onAfterPrevArrowClick()
  }
  const goToNextSlide = () => {
    const hypotheticalSlideIndex = currentSlide + numberOfSlides
    const isHypotheticalIndexHigherThanLastItemsInViewport = hypotheticalSlideIndex > items.length - numberOfSlides

    if (isInfinite) {
      const newCurrentSlide = isHypotheticalIndexHigherThanLastItemsInViewport ? 0 : hypotheticalSlideIndex
      setCurrentSlide(newCurrentSlide)
      return
    }

    const newCurrentSlide = isHypotheticalIndexHigherThanLastItemsInViewport
      ? items.length - numberOfSlides
      : hypotheticalSlideIndex

    setCurrentSlide(newCurrentSlide)
  }
  const onNextClick = () => {
    handleUserInteraction()
    goToNextSlide()
    onAfterNextArrowClick()
  }
  const onSlide = ({index}) => {
    setCurrentSlide(index)
  }

  const isPrevButtonDisabled = !isInfinite && currentSlide <= defaultSlide
  const isLastSlide = currentSlide >= items.length - numberOfSlides
  const isNextButtonDisabled = !isInfinite && isLastSlide
  const showExternalArrows = hasExternalArrows && items.length > numberOfSlides
  const shouldRenderHeading = title || titleNode || showExternalArrows
  const carouselProps = {
    arrowLeftLabel: i18n.t('COMMON.PREVIOUS'),
    arrowRightLabel: i18n.t('COMMON.NEXT'),
    isFullWidth,
    ...(itemsToPreload && {itemsToPreload}),
    numOfSlides: numberOfSlides,
    onSlide,
    slide: currentSlide,
    hasInfiniteLoop: isInfinite,
    ...(hasExternalArrows && {
      arrowLeft: NULLISH_COMPONENT,
      arrowRight: NULLISH_COMPONENT
    })
  }

  const intervalRef = useRef(null)

  useEffect(() => {
    if (!autoplay || userHasInteracted) return
    function handleAutoplay() {
      if (!isInfinite && isLastSlide) return clearInterval(intervalRef.current)
      goToNextSlide()
    }
    intervalRef.current = setInterval(handleAutoplay, autoplayInterval)
    return () => clearInterval(intervalRef.current)
  }, [
    autoplay,
    autoplayInterval,
    currentSlide,
    items.length,
    numberOfSlides,
    userHasInteracted,
    goToNextSlide,
    goToPrevSlide,
    isInfinite,
    isLastSlide
  ])

  return (
    <div className={BASE_CLASS}>
      {shouldRenderHeading && (
        <div className={HEADING_CLASS}>
          {titleNode ||
            (title && (
              <TitleBasic as={titleBasicTags.h2} color={titleBasicColors.black} size={titleSize} title={title} />
            ))}

          {showExternalArrows && (
            <div className={ARROWS_CLASS}>
              <AtomButton
                design={atomButtonDesigns.OUTLINE}
                shape={atomButtonShapes.CIRCULAR}
                size={atomButtonSizes.SMALL}
                onClick={onPrevClick}
                disabled={isPrevButtonDisabled}
                data-testid="button-previous-slide"
              >
                <IconAngleLeft size={atomIconSizes.small} aria-hidden role="presentation" />
              </AtomButton>
              <AtomButton
                design={atomButtonDesigns.OUTLINE}
                shape={atomButtonShapes.CIRCULAR}
                size={atomButtonSizes.SMALL}
                onClick={onNextClick}
                disabled={isNextButtonDisabled}
                data-testid="button-next-slide"
              >
                <IconAngleRight size={atomIconSizes.small} aria-hidden role="presentation" />
              </AtomButton>
            </div>
          )}
        </div>
      )}

      <MoleculeCarousel {...carouselProps}>{items}</MoleculeCarousel>
    </div>
  )
}

export {titleBasicSizes as listSlidesTitleSizes}

ListSlides.displayName = 'ListSlides'
ListSlides.propTypes = {
  defaultSlide: PropTypes.number,
  hasExternalArrows: PropTypes.bool,
  isFullWidth: PropTypes.bool,
  items: PropTypes.array.isRequired,
  itemsToPreload: PropTypes.number,
  numberOfSlides: PropTypes.number,
  onAfterNextArrowClick: PropTypes.func,
  onAfterPrevArrowClick: PropTypes.func,
  title: PropTypes.string,
  titleNode: PropTypes.node,
  titleSize: PropTypes.string,
  autoplay: PropTypes.bool,
  autoplayInterval: PropTypes.number,
  isInfinite: PropTypes.bool
}
